
// @ts-nocheck
import { supabase } from "@/integrations/supabase/client";

export interface TopStoriesResponse {
  Novidades: Array<{
    id: number;
    created_at: string;
    title: string;
    summary: string;
    cover_url: string | null;
    published: boolean;
    finished: boolean;
    author: {
      id: number;
      pen_name: string;
      full_name: string;
      profile_pic: string | null;
      status: string;
    };
  }>;
  Populares: Array<{
    id: number;
    title: string;
    summary: string;
    cover_url: string | null;
    published: boolean;
    finished: boolean;
    reads_7d: number;
    author: {
      id: number;
      pen_name: string;
      full_name: string;
      profile_pic: string | null;
      status: string;
    };
  }>;
  window_days: number;
}

export interface TopIPsResponse {
  TopIPs: Array<{
    id: number;
    title: string;
    summary: string;
    cover_url: string;
    published: boolean;
    finished: boolean;
    available_ip: boolean;
    fanfic_sessions: number;
    chat_sessions: number;
    unique_users_active: number;
    score: number;
    author: {
      id: number;
      pen_name: string;
      full_name: string;
      profile_pic: string;
      status: string;
    };
  }>;
  window_days: number;
  chat_source: string;
}

export const getTopIPs = async (): Promise<TopIPsResponse> => {
  try {
    const SUPABASE_URL = "https://zbvjxryqzrygkixfuidj.supabase.co";
    const url = `${SUPABASE_URL}/functions/v1/get-top-ips`;

    const { data: { session } } = await supabase.auth.getSession();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: headers
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch top IPs: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching top IPs:', error);
    throw error;
  }
};

// Interfaces for All IPs
export interface AllIPsResponse {
  items: {
    id: number;
    title: string;
    summary: string;
    cover_url: string;
    published: boolean;
    finished: boolean;
    available_ip: boolean;
    fanfic_sessions?: number;
    chat_sessions?: number;
    author: {
      id: number;
      pen_name: string;
      full_name: string;
      profile_pic: string;
      status: string;
    };
  }[];
  meta: {
    count: number;
    limit: number;
    offset: number;
    order_by: string;
    order_dir: string;
    filters: any;
  };
}

export const getAllIPs = async (options: {
  offset?: number;
  limit?: number;
  search?: string;
  available_ip?: boolean;
  published?: boolean;
} = {}): Promise<AllIPsResponse> => {
  try {
    const SUPABASE_URL = "https://zbvjxryqzrygkixfuidj.supabase.co";
    const params = new URLSearchParams();
    
    if (options.offset !== undefined) params.append('offset', options.offset.toString());
    if (options.limit !== undefined) params.append('limit', options.limit.toString());
    if (options.search) params.append('term', options.search);
    if (options.available_ip !== undefined) params.append('available_ip', options.available_ip.toString());
    if (options.published !== undefined) params.append('published', options.published.toString());

    const url = `${SUPABASE_URL}/functions/v1/list-all-ips?${params.toString()}`;

    const { data: { session } } = await supabase.auth.getSession();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: headers
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch all IPs: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching all IPs:', error);
    throw error;
  }
};

export const getTopStories = async (): Promise<TopStoriesResponse> => {
  try {
    const SUPABASE_URL = "https://zbvjxryqzrygkixfuidj.supabase.co";
    const url = `${SUPABASE_URL}/functions/v1/get-top-stories`;

    const { data: { session } } = await supabase.auth.getSession();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data as TopStoriesResponse;
  } catch (error) {
    console.error("Error fetching top stories:", error);
    throw error;
  }
};

export const fetchAvailableStoriesFanfic = async (q?: string, limit: number = 12, offset: number = 0) => {
  try {
    const SUPABASE_URL = "https://zbvjxryqzrygkixfuidj.supabase.co";
    const url = new URL(`${SUPABASE_URL}/functions/v1/get-available-stories-fanfic`);
    
    if (q) url.searchParams.append('q', q);
    url.searchParams.append('limit', limit.toString());
    url.searchParams.append('offset', offset.toString());

    const { data: { session } } = await supabase.auth.getSession();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers,
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const payload = await response.json();
    const items = Array.isArray(payload) ? payload : (payload?.items ?? []);
    
    // Transform data to match Story interface
    const stories = items.map((story: any) => ({
      id: String(story.id),
      title: story.title,
      authorName: story.author?.pen_name || story.authors?.pen_name || 'Autor',
      coverImage: story.cover_url || '/placeholder.svg'
    }));
    
    return stories;
  } catch (error) {
    console.error("Error fetching available stories for fanfic:", error);
    throw error;
  }
};

export const getStories = async () => {
  try {
    const { data, error } = await supabase
      .from('stories')
      .select('*, authors(pen_name, full_name)')
      .eq('published', true)  // Using lowercase 'published' column name
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error("Error fetching stories:", error);
      return { stories: null, error: error.message };
    }

    // Log returned stories to verify column names
    console.log("Retrieved stories data:", data ? data.length : 0, "items");
    if (data && data.length > 0) {
      console.log("First story publish status:", data[0].published);
    }

    return { stories: data, error: null };
  } catch (error) {
    const e = error as Error;
    console.error("Error in getStories:", e);
    return { stories: null, error: e.message };
  }
};

// Response interface for paginated stories
export interface AllStoriesResponse {
  items: any[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
  };
}

// New function to get all stories with optional filtering by author
export const getAllStories = async (authorId?: string) => {
  try {
    let query = supabase
      .from('stories')
      .select('*, authors(pen_name, full_name)')
      .eq('published', true)  // Using lowercase 'published' column name
      .order('created_at', { ascending: false });
    
    // Add author filtering if provided
    if (authorId && !isNaN(Number(authorId))) {
      query = query.eq('author_id', Number(authorId));
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error("Error fetching all stories:", error);
      return { stories: null, error: error.message };
    }

    return { stories: data, error: null };
  } catch (error) {
    const e = error as Error;
    console.error("Error in getAllStories:", e);
    return { stories: null, error: e.message };
  }
};

// Paginated function for infinite scroll
export const getAllStoriesWithPagination = async (offset: number = 0, limit: number = 20): Promise<AllStoriesResponse> => {
  try {
    // Get total count first
    const { count } = await supabase
      .from('stories')
      .select('*', { count: 'exact', head: true })
      .eq('published', true);

    // Get paginated data
    const { data, error } = await supabase
      .from('stories')
      .select('*, authors(pen_name, full_name)')
      .eq('published', true)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);
    
    if (error) {
      console.error("Error fetching paginated stories:", error);
      throw new Error(error.message);
    }

    const total = count || 0;
    const hasMore = offset + limit < total;

    return {
      items: data || [],
      pagination: {
        limit,
        offset,
        total,
        hasMore
      }
    };
  } catch (error) {
    const e = error as Error;
    console.error("Error in getAllStoriesWithPagination:", e);
    throw e;
  }
};

// New function to get all stories with their first 2 chapters for admin preview
export const getAllStoriesWithChapters = async (authorId?: string) => {
  try {
    let query = supabase
      .from('stories')
      .select(`
        *,
        authors(pen_name, full_name),
        chapters!inner(
          id,
          title,
          chapter_number,
          content,
          locked
        )
      `)
      .eq('published', true)
      .order('created_at', { ascending: false });
    
    // Add author filtering if provided
    if (authorId && !isNaN(Number(authorId))) {
      query = query.eq('author_id', Number(authorId));
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error("Error fetching all stories with chapters:", error);
      return { stories: null, error: error.message };
    }

    // Process the data to only include first 2 chapters and sort them
    const processedStories = data?.map(story => ({
      ...story,
      chapters: story.chapters
        ?.sort((a, b) => a.chapter_number - b.chapter_number)
        ?.slice(0, 2) || []
    })) || [];

    return { stories: processedStories, error: null };
  } catch (error) {
    const e = error as Error;
    console.error("Error in getAllStoriesWithChapters:", e);
    return { stories: null, error: e.message };
  }
};

// Updated function to get only published authors for the filter
export const getAllAuthors = async () => {
  try {
    const { data, error } = await supabase
      .from('authors')
      .select('id, pen_name, full_name')
      .eq('status', 'published')  // Filter by published status
      .order('pen_name', { ascending: true });
    
    if (error) {
      console.error("Error fetching authors:", error);
      return { authors: null, error: error.message };
    }

    return { authors: data, error: null };
  } catch (error) {
    const e = error as Error;
    console.error("Error in getAllAuthors:", e);
    return { authors: null, error: e.message };
  }
};

export const getStoryById = async (id: string) => {
  try {
    // Convert string id to number since the database expects a number
    const numericId = parseInt(id, 10);
    
    if (isNaN(numericId)) {
      return { story: null, error: "Invalid story ID" };
    }
    
    const { data, error } = await supabase
      .from('stories')
      .select('*, authors(id, pen_name, full_name, profile_pic)')
      // @ts-ignore – suppress deep type instantiation for filter
      .eq('id', numericId)
      .eq('published', true)  // Updated from 'Published' to 'published'
      .single();
    
    if (error) {
      console.error("Error fetching story:", error);
      return { story: null, error: error.message };
    }

    // Log the returned data for debugging
    console.log("Story data retrieved:", data);
    console.log("Author data in story:", data?.authors);

    return { story: data, error: null };
  } catch (error) {
    const e = error as Error;
    console.error("Error in getStoryById:", e);
    return { story: null, error: e.message };
  }
};

export const getChaptersByStoryId = async (storyId: string) => {
  try {
    // Convert string id to number
    const numericId = parseInt(storyId, 10);
    
    if (isNaN(numericId)) {
      return { chapters: null, error: "Invalid story ID" };
    }
    
    // First check if the story is published
    const { data: story, error: storyError } = await supabase
      .from('stories')
      .select('published')
      .eq('id', numericId)
      .single();
      
    if (storyError) {
      console.error("Error checking story published status:", storyError);
      return { chapters: null, error: storyError.message };
    }
    
    if (!story || !story.published) {
      return { chapters: null, error: "Story not found or not published" };
    }
    
    const { data, error } = await supabase
      .from('chapters')
      .select('*')
      .eq('story_id', numericId)
      .order('chapter_number', { ascending: true });
    
    if (error) {
      console.error("Error fetching chapters:", error);
      return { chapters: null, error: error.message };
    }

    return { chapters: data, error: null };
  } catch (error) {
    const e = error as Error;
    console.error("Error in getChaptersByStoryId:", e);
    return { chapters: null, error: e.message };
  }
};

export const getChapterById = async (id: string) => {
  try {
    // Convert string id to number
    const numericId = parseInt(id, 10);
    
    if (isNaN(numericId)) {
      return { chapter: null, error: "Invalid chapter ID" };
    }
    
    console.log(`Fetching chapter with ID ${numericId}`);
    
    const { data, error } = await supabase
      .from('chapters')
      .select('*, stories!inner(*, authors(id, pen_name, full_name, profile_pic))')
      .eq('id', numericId)
      .eq('stories.published', true)
      .single();
    
    if (error) {
      console.error("Error fetching chapter:", error);
      return { chapter: null, error: error.message };
    }

    // Enhanced logging for debugging
    console.log("Chapter data retrieved:", {
      id: data.id,
      title: data.title,
      story_id: data.story_id,
      storyTitle: data?.stories?.title,
    });

    console.log("Author data structure:", data?.stories?.authors);

    // Validate author data structure
    if (data?.stories?.authors) {
      const author = data.stories.authors;
      console.log("Author name fields:", {
        pen_name: author.pen_name,
        full_name: author.full_name
      });
    } else {
      console.warn("No author data found in chapter response");
    }

    return { chapter: data, error: null };
  } catch (error) {
    const e = error as Error;
    console.error("Error in getChapterById:", e);
    return { chapter: null, error: e.message };
  }
};

// Check if user has started reading any chapter of a story - Updated to support impersonation
export const hasUserStartedReadingStory = async (storyId: string, impersonatedUserId?: string) => {
  try {
    const numericStoryId = parseInt(storyId, 10);
    
    if (isNaN(numericStoryId)) {
      return { hasStarted: false, error: "Invalid story ID" };
    }
    
    // First check if the story is published
    const { data: story, error: storyError } = await supabase
      .from('stories')
      .select('published')
      .eq('id', numericStoryId)
      .single();
      
    if (storyError) {
      console.error("Error checking story published status:", storyError);
      return { hasStarted: false, error: storyError.message };
    }
    
    if (!story || !story.published) {
      return { hasStarted: false, error: "Story not found or not published" };
    }
    
    // If impersonatedUserId is provided, use it directly; otherwise, get the current user
    let userId: string | undefined;
    
    if (impersonatedUserId) {
      userId = impersonatedUserId;
      console.log("Checking if impersonated user has started reading story:", storyId);
    } else {
      // Get the current authenticated user
      const { data: { user } } = await supabase.auth.getUser();
      userId = user?.id;
    }
    
    if (!userId) {
      return { hasStarted: false, error: null };
    }
    
    console.log(`Checking reading status for user ${userId} and story ${storyId}`);
    
    const { data, error } = await supabase
      .from('chapter_sessions')
      .select('chapter_id, percent_read')
      .eq('user_id', userId)
      .gt('percent_read', 0)
      .order('created_at', { ascending: false });
      
    if (error) {
      console.error("Error checking reading status:", error);
      return { hasStarted: false, error: error.message };
    }

    if (!data || data.length === 0) {
      console.log("No reading sessions found for this user");
      return { hasStarted: false, error: null };
    }
    
    // We need to check if any of these chapters belong to the current story
    const chapterIds = data.map(session => session.chapter_id);
    
    const { data: chapters, error: chaptersError } = await supabase
      .from('chapters')
      .select('id')
      .eq('story_id', numericStoryId)
      .in('id', chapterIds);
      
    if (chaptersError) {
      console.error("Error checking chapters:", chaptersError);
      return { hasStarted: false, error: chaptersError.message };
    }
    
    const result = chapters && chapters.length > 0;
    console.log(`Has user started reading this story? ${result}`);
    
    return { 
      hasStarted: result, 
      error: null 
    };
    
  } catch (error) {
    const e = error as Error;
    console.error("Error in hasUserStartedReadingStory:", e);
    return { hasStarted: false, error: e.message };
  }
};

// New function to get the last read chapter for a story - Updated for impersonation
export const getLastReadChapter = async (storyId: string, impersonatedUserId?: string) => {
  try {
    const numericStoryId = parseInt(storyId, 10);
    
    if (isNaN(numericStoryId)) {
      return { chapterId: null, error: "Invalid story ID" };
    }
    
    // First check if the story is published
    const { data: story, error: storyError } = await supabase
      .from('stories')
      .select('published')
      .eq('id', numericStoryId)
      .single();
      
    if (storyError) {
      console.error("Error checking story published status:", storyError);
      return { chapterId: null, error: storyError.message };
    }
    
    if (!story || !story.published) {
      return { chapterId: null, error: "Story not found or not published" };
    }
    
    // If impersonatedUserId is provided, use it directly; otherwise, get the current user
    let userId: string | undefined;
    
    if (impersonatedUserId) {
      userId = impersonatedUserId;
      console.log("Getting last read chapter for impersonated user:", userId);
    } else {
      // Get the current authenticated user
      const { data: { user } } = await supabase.auth.getUser();
      userId = user?.id;
    }
    
    if (!userId) {
      return { chapterId: null, error: "User not authenticated" };
    }
    
    console.log(`Getting last read chapter for story ${numericStoryId} and user ${userId}`);
    
    // Get the most recent chapter session for this story
    const { data, error } = await supabase
      .from('chapter_sessions')
      .select('chapter_id, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
      
    if (error) {
      console.error("Error fetching chapter sessions:", error);
      return { chapterId: null, error: error.message };
    }
    
    if (!data || data.length === 0) {
      console.log("No chapter sessions found for this user");
      return { chapterId: null, error: null };
    }
    
    // We need to find which of these sessions belong to chapters of our story
    const { data: storyChapters, error: chaptersError } = await supabase
      .from('chapters')
      .select('id')
      .eq('story_id', numericStoryId);
      
    if (chaptersError) {
      console.error("Error fetching story chapters:", chaptersError);
      return { chapterId: null, error: chaptersError.message };
    }
    
    if (!storyChapters || storyChapters.length === 0) {
      console.log("No chapters found for this story");
      return { chapterId: null, error: null };
    }
    
    const storyChapterIds = storyChapters.map(chapter => chapter.id);
    
    // Find the most recent session for a chapter that belongs to this story
    const lastReadChapter = data.find(session => 
      storyChapterIds.includes(session.chapter_id)
    );
    
    if (lastReadChapter) {
      console.log(`Found last read chapter: ${lastReadChapter.chapter_id}`);
      return { chapterId: lastReadChapter.chapter_id, error: null };
    } else {
      console.log("No matching chapter sessions for this story");
      return { chapterId: null, error: null };
    }
  } catch (error) {
    const e = error as Error;
    console.error("Error in getLastReadChapter:", e);
    return { chapterId: null, error: e.message };
  }
};

// Modified to support impersonation
export const getContinueReadingStories = async (impersonatedUserId?: string) => {
  try {
    // If we have an impersonatedUserId, use that directly
    let userId: string | undefined;
    
    if (impersonatedUserId) {
      // For impersonation, use the provided ID directly
      userId = impersonatedUserId;
      console.log("Fetching continue reading stories for impersonated user:", userId);
    } else {
      // For normal usage, get the current authenticated user
      const { data: { user } } = await supabase.auth.getUser();
      userId = user?.id;
    }
    
    if (!userId) {
      return { stories: [], error: null };
    }
    
    // Get the most recent reading sessions for each story, ordered by last accessed
    const { data, error } = await supabase
      .from('chapter_sessions')
      .select(`
        chapter_id,
        percent_read,
        last_position,
        updated_at,
        chapters:chapter_id(
          id,
          title,
          chapter_number,
          story_id,
          stories:story_id(
            id, 
            title, 
            cover_url,
            published,
            authors(pen_name, full_name)
          )
        )
      `)
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });
      
    if (error) {
      console.error("Error fetching reading sessions:", error);
      return { stories: [], error: error.message };
    }

    if (!data || data.length === 0) {
      return { stories: [], error: null };
    }
    
    // Process and deduplicate stories (keep only most recent session per story)
    const storiesMap = new Map();
    
    data.forEach(session => {
      // Check if the story exists and is published
      if (session.chapters?.stories && session.chapters.stories.published === true) {
        const storyId = session.chapters.stories.id;
        
        // Only add the story if it's not already in the map
        if (!storiesMap.has(storyId)) {
          const story = session.chapters.stories;
          const authorName = story.authors ? 
            (story.authors.pen_name || story.authors.full_name || 'Unknown Author') : 
            'Unknown Author';
          
          storiesMap.set(storyId, {
            id: storyId.toString(),
            title: story.title || 'Untitled',
            author: 'Unknown',
            authorName: authorName,
            coverImage: story.cover_url || '/placeholder.svg',
            chapterId: session.chapter_id,
            chapterTitle: session.chapters.title,
            chapterNumber: session.chapters.chapter_number,
            percentRead: session.percent_read,
            lastPosition: session.last_position,
            lastRead: session.updated_at
          });
        }
      }
    });
    
    // Convert map to array and take the first 4 stories
    const stories = Array.from(storiesMap.values()).slice(0, 4);
    
    return { stories, error: null };
  } catch (error) {
    const e = error as Error;
    console.error("Error in getContinueReadingStories:", e);
    return { stories: [], error: e.message };
  }
};

// New function to get story by ID for preview (public access)
export const getStoryByIdForPreview = async (id: string) => {
  try {
    // Convert string id to number since the database expects a number
    const numericId = parseInt(id, 10);
    
    if (isNaN(numericId)) {
      return { story: null, error: "Invalid story ID" };
    }
    
    const { data, error } = await supabase
      .from('stories')
      .select(`
        id,
        title,
        summary,
        cover_url,
        created_at,
        published,
        finished,
        macro_category,
        authors(id, pen_name, full_name, profile_pic, Bio)
      `)
      .eq('id', numericId)
      .eq('published', true)
      .single();
    
    if (error) {
      console.error("Error fetching story for preview:", error);
      return { story: null, error: error.message };
    }

    // Log the returned data for debugging
    console.log("Story preview data retrieved:", data);

    return { story: data, error: null };
  } catch (error) {
    const e = error as Error;
    console.error("Error in getStoryByIdForPreview:", e);
    return { story: null, error: e.message };
  }
};

// New function to get chapters for story preview (first few chapters only)
export const getChaptersForStoryPreview = async (storyId: string) => {
  try {
    // Convert string id to number
    const numericId = parseInt(storyId, 10);
    
    if (isNaN(numericId)) {
      return { chapters: null, error: "Invalid story ID" };
    }
    
    // First check if the story is published
    const { data: story, error: storyError } = await supabase
      .from('stories')
      .select('published')
      .eq('id', numericId)
      .single();
      
    if (storyError) {
      console.error("Error checking story published status:", storyError);
      return { chapters: null, error: storyError.message };
    }
    
    if (!story || !story.published) {
      return { chapters: null, error: "Story not found or not published" };
    }
    
    // Get first 5 chapters for preview with content
    const { data, error } = await supabase
      .from('chapters')
      .select('id, title, chapter_number, locked, content')
      .eq('story_id', numericId)
      .order('chapter_number', { ascending: true })
      .limit(5);
    
    if (error) {
      console.error("Error fetching chapters for preview:", error);
      return { chapters: null, error: error.message };
    }

    return { chapters: data, error: null };
  } catch (error) {
    const e = error as Error;
    console.error("Error in getChaptersForStoryPreview:", e);
    return { chapters: null, error: e.message };
  }
};

// New function to get story like count (for preview pages)
export const getStoryLikeCount = async (storyId: string) => {
  try {
    const numericId = parseInt(storyId, 10);
    
    if (isNaN(numericId)) {
      return { count: 0, error: "Invalid story ID" };
    }
    
    // Get the count of likes for the story
    const { count, error } = await supabase
      .from('story_likes')
      .select('*', { count: 'exact', head: true })
      .eq('story_id', numericId);
    
    if (error) {
      console.error("Error fetching story like count:", error);
      return { count: 0, error: error.message };
    }

    return { count: count || 0, error: null };
  } catch (error) {
    const e = error as Error;
    console.error("Error in getStoryLikeCount:", e);
    return { count: 0, error: e.message };
  }
};

// New function to get story comment count (for preview pages)
export const getStoryCommentCount = async (storyId: string) => {
  try {
    const numericId = parseInt(storyId, 10);
    
    if (isNaN(numericId)) {
      return { count: 0, error: "Invalid story ID" };
    }
    
    // Get the count of comments for the story
    const { count, error } = await supabase
      .from('story_comments')
      .select('*', { count: 'exact', head: true })
      .eq('story_id', numericId);
    
    if (error) {
      console.error("Error fetching story comment count:", error);
      return { count: 0, error: error.message };
    }

    return { count: count || 0, error: null };
  } catch (error) {
    const e = error as Error;
    console.error("Error in getStoryCommentCount:", e);
    return { count: 0, error: e.message };
  }
};

// New function to get story tropes
export const getStoryTropes = async (storyId: string) => {
  try {
    const numericId = parseInt(storyId, 10);
    
    if (isNaN(numericId)) {
      return { tropes: null, error: "Invalid story ID" };
    }
    
    const { data, error } = await supabase
      .from('story_tropes')
      .select(`
        tropes (
          id,
          name
        )
      `)
      .eq('story_id', numericId);
    
    if (error) {
      console.error("Error fetching story tropes:", error);
      return { tropes: null, error: error.message };
    }

    // Extract trope names from the nested structure
    const tropeNames = data?.map(item => item.tropes?.name).filter(Boolean) || [];
    
    return { tropes: tropeNames, error: null };
  } catch (error) {
    const e = error as Error;
    console.error("Error in getStoryTropes:", e);
    return { tropes: null, error: e.message };
  }
};

// New function to get story highlights for a user
export const getStoryHighlights = async (storyId: string, userId: string) => {
  const tLabel = `getStoryHighlights storyId=${storyId} userId=${userId}`;
  console.log(`[DEBUG] ${tLabel} - start`);
  console.time(tLabel);
  try {
    const numericId = parseInt(storyId, 10);
    
    if (isNaN(numericId)) {
      console.warn(`[DEBUG] getStoryHighlights - invalid storyId:`, storyId);
      console.timeEnd(tLabel);
      return { highlights: null, error: "Invalid story ID" };
    }
    
    const { data, error } = await supabase
      .from('text_highlights')
      .select(`
        id,
        selected_text,
        start_offset,
        end_offset,
        highlight_color,
        annotation,
        created_at,
        chapters:chapter_id!inner (
          id,
          title,
          chapter_number,
          story_id
        )
      `)
      .eq('user_id', userId)
      .eq('chapters.story_id', numericId)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error("[DEBUG] Error fetching story highlights:", error);
      console.timeEnd(tLabel);
      return { highlights: null, error: error.message };
    }

    const count = data?.length || 0;
    console.log(`[DEBUG] ${tLabel} - success, ${count} highlights`);
    console.timeEnd(tLabel);

    const normalized = (data || []).map((h: any) => ({
      ...h,
      chapters: {
        id: h.chapters?.id,
        title: h.chapters?.title,
        chapter_number: h.chapters?.chapter_number,
      },
    }));

    return { highlights: normalized, error: null };
  } catch (error) {
    const e = error as Error;
    console.error("[DEBUG] Error in getStoryHighlights:", e);
    console.timeEnd(tLabel);
    return { highlights: null, error: e.message };
  }
};
